import React, { useState, useEffect, use } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  TextField,
  List,
  ListItem,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Alert,
  CircularProgress,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { getCredentialPanelStyles } from "../../../../../theme/panelStyles";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { ottoCredentialsApi } from "../../../../../services/api/multi/otto_credentials";

const OttoCredentials = () => {
  const theme = useTheme();
  const cp = getCredentialPanelStyles(theme);

  const scopesList = [
    { display: "📦 Products", value: "products" },
    { display: "📋 Orders", value: "orders" },
    { display: "🧾 Receipts", value: "receipts" },
    { display: "↩️ Returns", value: "returns" },
    { display: "💰 Price Reduction", value: "price-reduction" },
    { display: "📦 Shipments", value: "shipments" },
    { display: "🚚 Shipping Profiles", value: "shipping-profiles" },
    { display: "📊 Availability", value: "availability" },
    { display: "🏭 Returns Warehouse Read", value: "returns-warehouse-read" },
    { display: "🏭 Returns Warehouse Write", value: "returns-warehouse-write" },
  ];
  const [loading, setLoading] = useState(false);
  const [credentials, setCredentials] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [formTestStatus, setFormTestStatus] = useState("");
  const [editingCredential, setEditingCredential] = useState(null);
  const [authStatus, setAuthStatus] = useState("Waiting for authentication...");
  // Form states
  const [credentialForm, setCredentialForm] = useState({
    display_name: "",
    version: "v4",
    client_id: "",
    client_secret: "",
    selectedScopes: [],
  });

  const [activeCredential, setActiveCredential] = useState(() => {
    const saved = localStorage.getItem("activeCredential");
    return saved ? JSON.parse(saved) : null;
  });

  // Dialog states
  const [credentialDialog, setCredentialDialog] = useState(false);

  useEffect(() => {
    loadCredentials();
  }, []);

  const loadCredentials = async (forceRefresh = false) => {
    try {
      setLoading(true);
      const CACHE_KEY = "otto_credentials_cache";
      const CACHE_DURATION = 300 * 1000; // 300 seconds

      if (!forceRefresh) {
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
          try {
            const { data, timestamp } = JSON.parse(cached);
            if (Date.now() - timestamp < CACHE_DURATION) {
              setCredentials(data);
              // Test all credentials in parallel
              const testPromises = data.map((credential) =>
                testCredentialStatus(credential),
              );
              await Promise.allSettled(testPromises);
              setLoading(false);
              return;
            }
          } catch (e) {
            console.error("Error parsing cache", e);
            localStorage.removeItem(CACHE_KEY);
          }
        }
      }

      const response = await ottoCredentialsApi.getCredentials();
      const credentialList = response.data || [];
      setCredentials(credentialList);

      localStorage.setItem(
        CACHE_KEY,
        JSON.stringify({
          data: credentialList,
          timestamp: Date.now(),
        }),
      );

      // Test all credentials in parallel
      const testPromises = credentialList.map((credential) =>
        testCredentialStatus(credential),
      );
      await Promise.allSettled(testPromises);
    } catch (error) {
      console.error("Failed to load credentials:", error);
      setError("Failed to load credentials: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleActiveCredential = async (credential) => {
    if (credential.status !== "Connected") {
      setError("Only connected credentials can be activated");
      return;
    }

    try {
      setLoading(true);

      // Call backend to toggle status (backend ensures only one active)
      const response = await ottoCredentialsApi.toggleCredentialStatus(
        credential.id,
      );

      if (response.data.active) {
        // This credential was activated
        setActiveCredential(credential);
        localStorage.setItem("activeCredential", JSON.stringify(credential));
        setSuccess(`Activated credential: ${credential.display_name}`);

        // Dispatch custom event for other tabs to react
        window.dispatchEvent(new Event("activeCredentialChanged"));
      } else {
        // This credential was deactivated
        setActiveCredential(null);
        localStorage.removeItem("activeCredential");
        setSuccess("Credential deactivated");

        // Dispatch custom event for other tabs to react
        window.dispatchEvent(new Event("activeCredentialChanged"));
      }

      // Reload credentials to update UI for all credentials
      await loadCredentials(true);
    } catch (error) {
      setError("Failed to toggle credential status: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const testCredentialStatus = async (credential) => {
    try {
      const response = await ottoCredentialsApi.testCredentialsbyId(
        credential.id,
      );

      // Update the credential status in the state
      setCredentials((prev) =>
        prev.map((cred) =>
          cred.id === credential.id
            ? {
                ...cred,
                status: response.data.authorized ? "Connected" : "Disconnected",
              }
            : cred,
        ),
      );
    } catch (error) {
      console.error(`Failed to test credential ${credential.id}:`, error);
      // Update status to disconnected on error
      setCredentials((prev) =>
        prev.map((cred) =>
          cred.id === credential.id
            ? { ...cred, status: "Disconnected" }
            : cred,
        ),
      );
    }
  };

  const handleCredentialSubmit = async () => {
    if (credentialForm.display_name && credentialForm.client_id) {
      try {
        setLoading(true);

        if (editingCredential) {
          // Update existing credential
          await ottoCredentialsApi.updateCredential(editingCredential.id, {
            ...credentialForm,
            scopes: credentialForm.selectedScopes.join(" "),
          });
          setSuccess("Credential updated successfully!");
        } else {
          // Create new credential
          await ottoCredentialsApi.createCredential({
            ...credentialForm,
            scopes: credentialForm.selectedScopes.join(" "),
          });
          setSuccess("Credential created successfully!");
        }

        await loadCredentials(true); // Reload credentials
        setCredentialForm({
          display_name: "",
          version: "v4",
          client_id: "",
          client_secret: "",
          selectedScopes: [],
        });
        setEditingCredential(null); // Clear editing state
        setCredentialDialog(false);
        setFormTestStatus(""); // Clear test status
      } catch (error) {
        setError(
          `Failed to ${editingCredential ? "update" : "create"} credential: ` +
            error.message,
        );
      } finally {
        setLoading(false);
      }
    }
  };

  const testCredentialsFromForm = async () => {
    if (!credentialForm.client_id || !credentialForm.client_secret) {
      setFormTestStatus("❌ Please fill in Client ID and Client Secret first");
      return;
    }
    try {
      setLoading(true);
      setFormTestStatus("Testing...");
      const response = await ottoCredentialsApi.testCredentials({
        client_id: credentialForm.client_id,
        client_secret: credentialForm.client_secret,
        display_name: credentialForm.display_name,
        version: credentialForm.version,
        scopes: credentialForm.selectedScopes.join(" "),
      });

      if (response.data.authorized) {
        setFormTestStatus("✅ Authentication successful!");
      } else {
        setFormTestStatus("❌ Authentication failed!");
      }
    } catch (error) {
      setFormTestStatus("❌ Authentication failed: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const testCredentials = async (credential) => {
    try {
      setLoading(true);
      setAuthStatus("Testing...");
      const response = await ottoCredentialsApi.testCredentialsbyId(
        credential.id,
      );

      if (response.data.authorized) {
        setAuthStatus("✅ Authentication successful!");
        await loadCredentials(true); // Reload to get updated status
      } else {
        setAuthStatus("❌ Authentication failed!");
      }
    } catch (error) {
      setAuthStatus("❌ Authentication failed: " + error.message);
      setError("Failed to test credentials: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditCredential = (credential) => {
    setEditingCredential(credential);
    setCredentialForm({
      display_name: credential.display_name,
      version: credential.version,
      client_id: credential.client_id,
      client_secret: credential.client_secret,
      selectedScopes: credential.scopes ? credential.scopes.split(" ") : [],
    });
    setFormTestStatus(""); // Clear test status
    setCredentialDialog(true);
  };

  const deleteCredential = async (credentialId) => {
    try {
      setLoading(true);

      // Clear active credential if it's the one being deleted
      if (activeCredential?.id === credentialId) {
        setActiveCredential(null);
        localStorage.removeItem("activeCredential");
      }

      await ottoCredentialsApi.deleteCredential(credentialId);
      await loadCredentials(true);
      setSuccess("Credential deleted successfully!");
    } catch (error) {
      setError("Failed to delete credential: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card sx={cp.outerCard}>
            <CardContent sx={{ p: 4 }}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={3}
                sx={{
                  ...cp.header,
                  p: 4,
                  borderRadius: 2,
                  color: "white",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    marginRight: "auto",
                    paddingRight: 2,
                  }}
                >
                  <Typography variant="h5" sx={{ fontWeight: "bold", mb: 1 }}>
                    🔐 Otto API Credentials
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Manage your Otto API access credentials
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={loadCredentials}
                    disabled={loading}
                    sx={{
                      backgroundColor: "rgba(255, 255, 255, 0.2)",
                      backdropFilter: "blur(10px)",
                      border: "1px solid rgba(255, 255, 255, 0.3)",
                      color: "white",
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.3)",
                        transform: "translateY(-2px)",
                      },
                      transition: "all 0.3s ease",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Refresh
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      setCredentialForm({
                        display_name: "",
                        version: "v4",
                        client_id: "",
                        client_secret: "",
                        selectedScopes: [],
                      });
                      setCredentialDialog(true);
                    }}
                    sx={{
                      backgroundColor: "rgba(255, 255, 255, 0.2)",
                      backdropFilter: "blur(10px)",
                      border: "1px solid rgba(255, 255, 255, 0.3)",
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.3)",
                        transform: "translateY(-2px)",
                      },
                      transition: "all 0.3s ease",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Add Credentials
                  </Button>
                </Box>
              </Box>

              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>How to Get Your Otto Credentials:</strong>
                </Typography>
                <ol style={{ margin: 0, paddingLeft: "20px" }}>
                  <li>Register as a developer on the OTTO Market platform</li>
                  <li>
                    Create a new application in the "Applications" section
                  </li>
                  <li>
                    Obtain your Client ID and Client Secret from application
                    details
                  </li>
                  <li>Enter the credentials in the form below</li>
                  <li>
                    Make Sure the Scope includes the necessary permissions
                  </li>
                </ol>
                <Typography variant="body2" mt={1}>
                  For more information, visit{" "}
                  <a
                    href="https://portal.otto.market/settings/api-access#/production"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: "#1976d2",
                      textDecoration: "underline",
                      fontWeight: "bold",
                    }}
                  >
                    OTTO API Access
                  </a>
                </Typography>
              </Alert>

              {credentials.length === 0 ? (
                <Box sx={cp.emptyState}>
                  <Typography
                    variant="h6"
                    sx={{ mb: 2, color: "text.secondary" }}
                  >
                    No Credentials Found
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ mb: 3, color: "text.secondary" }}
                  >
                    Add your first Otto credential to get started
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      setCredentialForm({
                        display_name: "",
                        version: "v4",
                        client_id: "",
                        client_secret: "",
                        selectedScopes: [],
                      });
                      setCredentialDialog(true);
                    }}
                    sx={{
                      borderRadius: 3,
                      px: 4,
                      py: 1.5,
                      ...cp.ctaButton,
                    }}
                  >
                    Add Your First Credential
                  </Button>
                </Box>
              ) : (
                <List
                  sx={{
                    width: "100%",
                    border: "2px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    bgcolor: "background.paper",
                    boxShadow: 2,
                  }}
                >
                  {credentials.map((credential, index) => (
                    <ListItem
                      key={credential.id}
                      divider={index < credentials.length - 1}
                      sx={{
                        px: 4,
                        py: 3,
                        borderBottom:
                          index < credentials.length - 1 ? "2px solid" : "none",
                        borderColor: "divider",
                        "&:hover": {
                          backgroundColor: "action.hover",
                          transform: "translateX(4px)",
                        },
                        transition: "all 0.2s ease",
                        borderRadius:
                          index === 0
                            ? "8px 8px 0 0"
                            : index === credentials.length - 1
                              ? "0 0 8px 8px"
                              : 0,
                      }}
                    >
                      <Grid container alignItems="center" spacing={3}>
                        <Grid item xs={12} md={8}>
                          <Typography variant="h6">
                            {credential.display_name}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              wordBreak: "break-all",
                              pr: 2,
                            }}
                          >
                            Version: {credential.version} | Client ID:{" "}
                            {credential.client_id}
                          </Typography>
                          <Box
                            sx={{
                              mt: 0.5,
                              display: "flex",
                              gap: 1,
                              alignItems: "center",
                            }}
                          >
                            <Chip
                              label={credential.status}
                              size="small"
                              color={
                                credential.status === "Connected"
                                  ? "success"
                                  : "error"
                              }
                            />
                            {/* Active Status Chip */}
                            {activeCredential?.id === credential.id && (
                              <Chip
                                label="ACTIVE"
                                size="small"
                                color="primary"
                                variant="filled"
                              />
                            )}
                          </Box>
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          md={4}
                          sx={{
                            display: "flex",
                            justifyContent: { xs: "center", md: "flex-end" },
                            alignItems: "center",
                            gap: 0.5,
                            flexWrap: "wrap",
                          }}
                        >
                          {/* Active Toggle Switch */}
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              mr: 2,
                            }}
                          >
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={
                                    activeCredential?.id === credential.id
                                  }
                                  onChange={() =>
                                    toggleActiveCredential(credential)
                                  }
                                  disabled={credential.status !== "Connected"}
                                  size="small"
                                  sx={{
                                    "& .MuiSwitch-switchBase.Mui-checked": {
                                      color: "success.main",
                                    },
                                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                                      {
                                        backgroundColor: "success.main",
                                      },
                                  }}
                                />
                              }
                              label=""
                              sx={{ m: 0 }}
                            />
                            <Typography
                              variant="caption"
                              sx={{
                                color:
                                  activeCredential?.id === credential.id
                                    ? "success.main"
                                    : "text.secondary",
                                fontWeight: "bold",
                                mt: 0.5,
                              }}
                            >
                              {activeCredential?.id === credential.id
                                ? "Active"
                                : "Inactive"}
                            </Typography>
                          </Box>

                          <IconButton
                            onClick={() => testCredentials(credential)}
                            disabled={loading}
                            color="info"
                            title="Test Credentials"
                            sx={{
                              backgroundColor: "info.main",
                              color: "white",
                              "&:hover": {
                                backgroundColor: "info.dark",
                                transform: "scale(1.1)",
                              },
                              transition: "all 0.2s ease",
                            }}
                          >
                            {loading ? (
                              <CircularProgress size={20} />
                            ) : (
                              <CheckIcon />
                            )}
                          </IconButton>
                          <IconButton
                            title="Edit Credential"
                            onClick={() => handleEditCredential(credential)}
                            color="warning"
                            sx={{
                              backgroundColor: "warning.main",
                              color: "white",
                              "&:hover": {
                                backgroundColor: "warning.dark",
                                transform: "scale(1.1)",
                              },
                              transition: "all 0.2s ease",
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            color="error"
                            onClick={() => deleteCredential(credential.id)}
                            title="Delete Credential"
                            sx={{
                              backgroundColor: "error.main",
                              color: "white",
                              "&:hover": {
                                backgroundColor: "error.dark",
                                transform: "scale(1.1)",
                              },
                              transition: "all 0.2s ease",
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Grid>
                      </Grid>
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog
        open={credentialDialog}
        onClose={() => {
          setCredentialDialog(false);
          setEditingCredential(null);
          setFormTestStatus("");
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 24px 48px rgba(0,0,0,0.15)",
            ...cp.dialogPaper,
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1,
            borderBottom: "2px solid",
            borderColor: "divider",
            ...cp.dialogHeader,
            color: "white",
            fontWeight: 600,
            fontSize: "1.25rem",
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                bgcolor: "rgba(255, 255, 255, 0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {editingCredential ? <EditIcon /> : <AddIcon />}
            </Box>
            <Typography variant="h6" component="div" fontWeight={600}>
              {editingCredential
                ? "Edit Otto API Credentials"
                : "Add Otto API Credentials"}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2, px: 4 }}>
          <Alert severity="info" sx={{ mb: 3, mx: 0 }}>
            <Typography variant="body2" fontWeight={500}>
              Enter your Otto API credentials to connect to the Otto
              marketplace.
            </Typography>
          </Alert>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Display Name"
                placeholder="e.g., Otto Production API"
                value={credentialForm.display_name}
                onChange={(e) =>
                  setCredentialForm((prev) => ({
                    ...prev,
                    display_name: e.target.value,
                  }))
                }
                required
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>API Version</InputLabel>
                <Select
                  value={credentialForm.version}
                  label="API Version"
                  onChange={(e) =>
                    setCredentialForm((prev) => ({
                      ...prev,
                      version: e.target.value,
                    }))
                  }
                  sx={{
                    borderRadius: 2,
                  }}
                >
                  <MenuItem value="v3">v3</MenuItem>
                  <MenuItem value="v4">v4</MenuItem>
                  <MenuItem value="v5">v5</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Client ID"
                placeholder="Enter your Otto Client ID"
                value={credentialForm.client_id}
                onChange={(e) =>
                  setCredentialForm((prev) => ({
                    ...prev,
                    client_id: e.target.value,
                  }))
                }
                required
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    fontFamily: "monospace",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Client Secret"
                placeholder="Enter your Otto Client Secret"
                type="password"
                value={credentialForm.client_secret}
                onChange={(e) =>
                  setCredentialForm((prev) => ({
                    ...prev,
                    client_secret: e.target.value,
                  }))
                }
                required
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    fontFamily: "monospace",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Scopes
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={
                      credentialForm.selectedScopes.length === scopesList.length
                    }
                    indeterminate={
                      credentialForm.selectedScopes.length > 0 &&
                      credentialForm.selectedScopes.length < scopesList.length
                    }
                    onChange={(e) => {
                      if (e.target.checked) {
                        setCredentialForm((prev) => ({
                          ...prev,
                          selectedScopes: scopesList.map(
                            (scope) => scope.value,
                          ),
                        }));
                      } else {
                        setCredentialForm((prev) => ({
                          ...prev,
                          selectedScopes: [],
                        }));
                      }
                    }}
                  />
                }
                label="Select All Scopes"
                sx={{ mb: 1 }}
              />
              <Grid container spacing={1}>
                {scopesList.map((scope) => (
                  <Grid item xs={12} key={scope.value}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={credentialForm.selectedScopes.includes(
                            scope.value,
                          )}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setCredentialForm((prev) => ({
                                ...prev,
                                selectedScopes: [
                                  ...prev.selectedScopes,
                                  scope.value,
                                ],
                              }));
                            } else {
                              setCredentialForm((prev) => ({
                                ...prev,
                                selectedScopes: prev.selectedScopes.filter(
                                  (s) => s !== scope.value,
                                ),
                              }));
                            }
                          }}
                        />
                      }
                      label={scope.display}
                    />
                  </Grid>
                ))}
              </Grid>
            </Grid>
            {/* Add test status display */}
            {formTestStatus && (
              <Grid item xs={12}>
                <Alert
                  severity={
                    formTestStatus.includes("✅")
                      ? "success"
                      : formTestStatus.includes("Testing")
                        ? "info"
                        : "error"
                  }
                  sx={{
                    borderRadius: 2,
                    fontWeight: 500,
                  }}
                  icon={
                    formTestStatus.includes("Testing") ? (
                      <CircularProgress size={20} />
                    ) : undefined
                  }
                >
                  {formTestStatus}
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2.5,
            borderTop: "2px solid",
            borderColor: "divider",
            gap: 1,
            justifyContent: "space-between",
            backgroundColor: "grey.50",
            borderRadius: "0 0 12px 12px",
          }}
        >
          <Button
            onClick={() => {
              setCredentialDialog(false);
              setEditingCredential(null);
              setFormTestStatus("");
            }}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 500,
            }}
          >
            Cancel
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            onClick={testCredentialsFromForm}
            variant="outlined"
            startIcon={loading ? <CircularProgress size={16} /> : <CheckIcon />}
            disabled={
              loading ||
              !credentialForm.client_id ||
              !credentialForm.client_secret
            }
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 500,
              minWidth: 140,
            }}
          >
            Test Credentials
          </Button>
          <Button
            onClick={handleCredentialSubmit}
            variant="contained"
            disabled={
              !credentialForm.display_name ||
              !credentialForm.client_id ||
              !credentialForm.client_secret
            }
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 500,
              minWidth: 100,
              ...cp.ctaButton,
              "&:hover": {
                background: "linear-gradient(45deg, #1976D2 30%, #1CB5E0 90%)",
                transform: "translateY(-2px)",
              },
              "&:disabled": {
                background: "grey.400",
                color: "white",
              },
              transition: "all 0.3s ease",
            }}
          >
            {editingCredential ? "Update" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default OttoCredentials;
